import { createPartDefinition, type HomePartDefinition } from "@/home/storage";

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("โหลดภาพชิ้นไม่สำเร็จ"));
    img.src = src;
  });
}

function pickSmallerDataUrl(candidates: string[]): string {
  let best = candidates[0] ?? "";
  for (const url of candidates) {
    if (url.length > 0 && url.length < best.length) best = url;
  }
  return best;
}

/** Scale then encode; spritesheets stay PNG (pixel art), decals prefer WebP/JPEG. */
export async function compressImageDataUrl(
  dataUrl: string,
  maxEdge: number,
  opts: { pixelArt?: boolean },
): Promise<string> {
  if (!dataUrl.startsWith("data:image")) return dataUrl;
  const img = await loadImage(dataUrl);
  const iw = img.naturalWidth || 1;
  const ih = img.naturalHeight || 1;
  const scale = Math.min(1, maxEdge / Math.max(iw, ih));
  const outW = Math.max(1, Math.round(iw * scale));
  const outH = Math.max(1, Math.round(ih * scale));
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return dataUrl;
  ctx.imageSmoothingEnabled = !opts.pixelArt;
  ctx.drawImage(img, 0, 0, outW, outH);

  const png = canvas.toDataURL("image/png");
  if (opts.pixelArt) return png;

  const webp = canvas.toDataURL("image/webp", 0.82);
  const jpeg = canvas.toDataURL("image/jpeg", 0.85);
  return pickSmallerDataUrl([png, webp, jpeg]);
}

/** Drop bulky optional fields; monsters keep combat sheet metadata only. */
export function pickPartForCloud(part: HomePartDefinition): HomePartDefinition {
  const base = {
    id: part.id,
    name: part.name,
    imageDataUrl: part.imageDataUrl,
    cellsW: part.cellsW,
    cellsH: part.cellsH,
    kind: part.kind,
    footprintW: part.footprintW,
    footprintH: part.footprintH,
    tags: part.tags,
    depthBias: part.depthBias,
    placeScale: part.placeScale,
    passage: part.passage,
    zones: part.zones,
    sockets: part.sockets,
    createdAt: part.createdAt,
  };
  if (part.kind === "monster") {
    return createPartDefinition({
      ...base,
      monsterLevel: part.monsterLevel,
      monsterBaseHp: part.monsterBaseHp,
      monsterBaseAttack: part.monsterBaseAttack,
      monsterBaseDefense: part.monsterBaseDefense,
      monsterBaseDodge: part.monsterBaseDodge,
      monsterXpReward: part.monsterXpReward,
      spritesheetDataUrl: part.spritesheetDataUrl,
      grid: part.grid,
    });
  }
  if (part.kind === "attack_dummy" && part.spritesheetDataUrl) {
    return createPartDefinition({
      ...base,
      spritesheetDataUrl: part.spritesheetDataUrl,
      grid: part.grid,
    });
  }
  return createPartDefinition(base);
}

export async function shrinkPartsForCloud(
  parts: HomePartDefinition[],
  maxEdge: number,
): Promise<HomePartDefinition[]> {
  return Promise.all(
    parts.map(async (part) => {
      const picked = pickPartForCloud(part);
      const imageDataUrl = await compressImageDataUrl(picked.imageDataUrl, maxEdge, {
        pixelArt: false,
      });
      let spritesheetDataUrl = picked.spritesheetDataUrl;
      if (spritesheetDataUrl?.startsWith("data:")) {
        spritesheetDataUrl = await compressImageDataUrl(spritesheetDataUrl, maxEdge * 2, {
          pixelArt: true,
        });
      }
      return createPartDefinition({
        ...picked,
        imageDataUrl,
        spritesheetDataUrl,
        grid: part.grid,
      });
    }),
  );
}
